<template>
  <div class="aula-gallery-container">
    <template v-if="!showAlbumDetailForUpload">
      <gallery-toolbar v-if="!isUploadParent" :parent="parent" :select-mode="selectMode" />
      <div class="gallery-container page-layout-padding">
        <mobile-topbar
          v-if="isMobile && selectMode && !isUploadParent"
          :text="$t('gallery.gallery')"
          :show-ok="isAnySelectedAlbums"
          :right-text="mobileTopbarRightText"
          :text-key="isUploadParent ? 'GALLERY_UPLOAD_PICTURES' : ''"
          :header-text-from-textkey="false"
          @rightTextClicked="onMobileTopbarRightTextClicked"
          @leftIconClicked="onMobileTopbarLeftIconClicked"
        />
        <Alerts v-if="isGalleryParent" class="mb-0 mb-lg-4 p-2 p-lg-0" :filtered-areas="notificationAreas" />
        <b-row class="mb-3">
          <b-col
            ><h1 v-if="!isMobile" class="left m-0">{{ $t('gallery.gallery') }}</h1></b-col
          >
        </b-row>
        <b-row class="gallery-header">
          <b-col class="d-flex flex-row justify-content-start gallery-multiselect">
            <AulaSwitchButton
              v-model="selectMode"
              class="mr-2"
              :aria-label="$t('label.select_more')"
              @change="toggleTagMode()"
            >
              {{ $t('label.select_more') }}
              <span v-if="selectMode" class="text-nowrap">
                ({{ $tc('label.total_selected', selectedAlbums.length) }})
              </span>
            </AulaSwitchButton>
            <template v-if="!isMobile && selectMode && !isUploadParent">
              <AulaButton
                class="action-button"
                variant="link"
                :disabled="isDownloading || !isAnySelectedAlbums"
                :aria-label="$t('button.download')"
                @click="onDownloadAlbumClicked"
              >
                <Icon :name="iconClassEnum.DOWNLOAD" />
                {{ $t('button.download') }}
                <AulaSpinner v-if="isDownloading" class="download-spinner" />
              </AulaButton>
              <AulaButton
                class="action-button"
                variant="link"
                :disabled="isDeleting || !canDeleteSelectedAlbum || !isAnySelectedAlbums"
                :aria-label="$t('button.delete')"
                @click="onDeleteAlbumClicked"
              >
                <Icon :name="iconClassEnum.BIN" />
                {{ $t('button.delete') }}
              </AulaButton>
            </template>
          </b-col>
          <b-col v-if="!selectMode" class="d-flex flex-row justify-content-end gallery-dropdown">
            <b-dropdown ref="sorter" class="dropdown-select align-self-end sorter" right>
              <template #button-content>
                <span class="filter-name">
                  {{ filterName }}
                </span>
                <i class="icon icon-Aula_down-arrow" aria-hidden="true" />
              </template>
              <template v-for="filter in filterConfigOptions">
                <b-dropdown-item :key="filter.value" :active="isFilterSelected(filter)" @click="doFilter(filter.value)">
                  <span v-if="isFilterSelected(filter)" class="sr-only">
                    {{ $getText('ARIA_GALLERY_FILTER_SELECTED') }}
                  </span>
                  {{ $getText(filter.name) }}
                </b-dropdown-item>
              </template>
            </b-dropdown>
            <b-dropdown
              ref="sorter"
              class="dropdown-select align-self-end sorter gallery-sort"
              toggle-class="sorter"
              right
            >
              <template #button-content>
                <div class="sort" :class="orderDirection" />
                <span class="sort-name" :aria-label="sortNameAriaLabel">
                  {{ sortName }}
                </span>
                <i class="icon icon-Aula_down-arrow" aria-hidden="true" />
              </template>
              <template v-for="sort in sortConfigOptions">
                <b-dropdown-item
                  :key="sort.value"
                  :class="sort.value === sortOn ? orderDirection : ''"
                  @click="doSort(sort.value, sort.name)"
                >
                  {{ $t(sort.name) }}
                </b-dropdown-item>
              </template>
            </b-dropdown>
          </b-col>
        </b-row>
        <hr v-if="!isMobile" />
        <ObservingContainer :options="observerOptions">
          <AlbumList
            :album-list="albums"
            :select-mode="selectMode"
            :parent="parent"
            :is-group-parent="isGroupParent"
            :is-loading="isLoading"
            :notifications="notifications"
            @onOpenAlbum="openAlbum"
            @onSelectAlbum="selectAlbum"
          />
          <ObservedTarget v-if="isObservable" @onIntersecting="onBottomReached" />
        </ObservingContainer>
      </div>
      <transition v-if="isMobile" name="slide-only">
        <GalleryManagementMobile
          v-if="isVisibleGalleryActionMobile"
          :topbar-header-text="$tc('label.total_selected', selectedAlbums.length)"
          :selected-albums="selectedAlbums"
          :is-downloading="isDownloading"
          :is-deleting="isDeleting"
          :can-delete="canDeleteSelectedAlbum"
          @onDownloadAlbum="onDownloadAlbumClicked"
          @onDeleteAlbum="onDeleteAlbumClicked"
          @onReturn="onReturnClicked"
        />
      </transition>
    </template>
    <album-detail
      v-if="isUploadParent && showAlbumDetailForUpload"
      :parent="parent"
      :my-media="showMyMediaAlbumForUpload"
      :album-id="selectedAlbumId"
      :institution-profile-ids-filter="getFilterInstProfileIds"
      @addMediaFromGallery="$emit('addMediaFromGallery')"
      @selectedMediaUpdated="$emit('selectedMediaUpdated', $event)"
      @hideAlbumDetailForEditor="handleAlbumDetailHidden"
    />
    <aula-modal
      ref="downloadAlbumWarningModal"
      ok-text="BUTTON_YES"
      :is-loading="isDownloading"
      @cancelClicked="$refs.downloadAlbumWarningModal.hide()"
      @okClicked="downloadAlbums"
    >
      <template v-if="isSingleSelectedAlbum">
        <p v-html="$t('warning.download_album_single', { name: selectedAlbumsTitle[0] })" />
      </template>
      <template v-else>
        <p v-html="$t('warning.download_album_multiple')" />
        <ul>
          <li v-for="(selectedAlbumTitle, index) in selectedAlbumsTitle" :key="index" class="mb-2">
            <strong>{{ selectedAlbumTitle }}</strong>
          </li>
        </ul>
      </template>
      <p v-html="$t('warning.download_album')" />
    </aula-modal>
    <aula-modal
      ref="deleteAlbumWarningModal"
      ok-text="BUTTON_YES"
      :is-loading="isDeleting"
      @cancelClicked="$refs.deleteAlbumWarningModal.hide()"
      @okClicked="deleteAlbums"
    >
      <template v-if="isSingleSelectedAlbum">
        <p v-html="$t('warning.delete_album_single', { name: selectedAlbumsTitle[0] })" />
      </template>
      <template v-else>
        <p v-html="$t('warning.delete_album_multiple')" />
        <ul>
          <li v-for="(selectedAlbumTitle, index) in selectedAlbumsTitle" :key="index" class="mb-2">
            <strong>{{ selectedAlbumTitle }}</strong>
          </li>
        </ul>
      </template>
      <p
        v-html="
          $t('warning.delete_album_1', {
            album: $tc('gallery.albums', selectedAlbums.length),
            the: $tc('label.the', selectedAlbums.length),
          })
        "
      />
      <p v-html="$t('warning.delete_album_2', { album: $tc('gallery.albums', selectedAlbums.length) })" />
    </aula-modal>
    <aula-modal
      ref="stepUpModal"
      ok-text="BUTTON_YES"
      @okClicked="redirectToStepUp"
      @cancelClicked="$refs.stepUpModal.hide()"
    >
      {{ $t('warning.action_step_up_required') }}
      <br />
      {{ $t('warning.continue') }}
    </aula-modal>
    <create-album
      v-if="
        $route.path.includes('galleri/opret') ||
        $route.path.includes('galleri/tilfoej') ||
        $route.path.includes('galleri/rediger')
      "
      :parent="parent"
    />
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex';
import { types } from '../../store/types/types';
import { portalRoles } from '../../../shared/enums/portalRoles';
import { parentTypes } from '../../../shared/enums/parentTypes.ts';
import axios from 'axios';
import GalleryToolbar from './GalleryToolbar';
import AlbumDetail from './AlbumDetail';
import CreateAlbum from './CreateAlbum';
import { notificationEventTypes } from '../../../shared/enums/notificationEventTypes';
import { orderDirections } from '../../../shared/enums/orderDirections';
import { albumSortType } from '../../../shared/enums/albumSortType';
import { Cookie } from '../../../shared/assets/plugins/cookie';
import { albumFilterType } from '../../../shared/enums/albumFilterType';
import ObservingContainer from '../../../shared/libs/intersection-observer/components/Container.vue';
import ObservedTarget from '../../../shared/libs/intersection-observer/components/Target.vue';
import Alerts from '../../../shared/components/Alerts.vue';
import { notificationAreas } from '../../../shared/enums/notificationAreas';
import { AlbumDownloader } from '../../../shared/services/AlbumDownloader.service';
import AulaSwitchButton from '../../../shared/components/AulaSwitchButton.vue';
import AulaButton from '../../../shared/components/AulaButton.vue';
import Icon from '../../../shared/components/Icon.vue';
import { iconClassEnum } from '../../../shared/enums/iconClassEnum';
import MobileTopbar from '../../../shared/components/MobileTopbar.vue';
import AlbumList from '../../../shared/PageFragments/Album/AlbumList.vue';
import GalleryManagementMobile from './GalleryManagementMobile.vue';

export default {
  components: {
    Icon,
    AulaButton,
    AulaSwitchButton,
    Alerts,
    ObservedTarget,
    ObservingContainer,
    GalleryToolbar,
    AlbumDetail,
    CreateAlbum,
    MobileTopbar,
    AlbumList,
    GalleryManagementMobile,
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      if (!vm.isProfileLoaded || vm.isPreviewMode) {
        return;
      }
      if (['albumDetails', 'myMedias', 'group-album-details'].includes(from.name) && !vm.hasMediaChanged) {
        return;
      }
      vm.refresh();
    });
  },
  props: {
    parent: { type: String, default: parentTypes.GALLERY },
    selectedInstitutionCodeForFilter: { type: String, required: false, default: '' },
  },
  data: function () {
    return {
      mobilePageComponent: 'album',
      selectAll: false,
      isLoading: false,
      currentIndex: -1,
      pageSize: 12,
      listSelectedAlbum: [],
      showAlbumDetailForUpload: false,
      showMyMediaAlbumForUpload: false,
      selectedAlbumId: '',
      isDownloading: false,
      defaultConfigSections: [
        {
          name: 'GALLERY_ALBUM',
          filter: 'all',
          portalRoles: [portalRoles.EMPLOYEE, portalRoles.CHILD, portalRoles.GUARDIAN],
        },
      ],
      filterConfigOptions: [
        {
          value: albumFilterType.ALL,
          name: 'GALLERY_FILTER_ALL',
        },
        {
          value: albumFilterType.MY_ALBUMS,
          name: 'MEDIA_FILTER_MY_MEDIA_ALBUMS',
        },
      ],
      parentTypes,
      albumSortType,
      albumFilterType,
      originalProfilePortalRole: null,
      loadGalleryCancelTokenSource: null,
      isFiltered: false,
      selectMode: false,
      isDeleting: false,
      isVisibleGalleryActionMobile: false,
    };
  },
  computed: {
    iconClassEnum() {
      return iconClassEnum;
    },
    ...mapGetters({
      isMobile: types.GET_IS_MOBILE,
      images: types.GET_IMAGES,
      profile: types.GET_CURRENT_PROFILE,
      date: types.GET_DATE,
      albums: types.GET_ALBUMS,
      moreAlbumsExist: types.GET_MORE_ALBUMS_EXIST,
      activeChildren: types.GET_ACTIVE_CHILDREN,
      activeGroup: types.GET_ACTIVE_GROUP,
      isProfileLoaded: types.IS_PROFILE_LOADED,
      isPreviewMode: types.GET_IS_PREVIEW_MODE,
      children: types.GET_CHILDREN,
      institutions: types.GET_INSTITUTIONS,
      activeInstitutions: types.GET_ACTIVE_INSTITUTIONS,
      notifications: types.GET_NOTIFICATIONS,
      hasMediaChanged: types.GET_HAS_MEDIA_CHANGED,
      activeAlbum: types.GET_ACTIVE_ALBUM,
      filterBy: types.GET_GALLERY_FILTER_BY,
      sortOn: types.GET_GALLERY_SORT_ON,
      orderDirection: types.GET_GALLERY_ORDER_DIRECTION,
      isSteppedUp: types.GET_GLOBAL_STEPPED_UP,
    }),
    filterName() {
      if (!this.isFiltered) {
        return this.$getText('GALLERY_FILTER');
      }
      const selectedFilter = this.filterConfigOptions.find(option => option.value === this.filterBy);
      return this.$getText(selectedFilter?.name);
    },
    sortName() {
      const selectedSort = this.sortConfigOptions.find(option => option.value === this.sortOn);
      return this.$t(selectedSort?.name);
    },
    sortNameAriaLabel() {
      const selectedSort = this.sortConfigOptions.find(option => option.value === this.sortOn);
      const orderSuffix = this.orderDirection === orderDirections.ASCENDING ? selectedSort?.asc : selectedSort?.dsc;
      return this.$t(selectedSort?.name) + this.$t(orderSuffix);
    },
    notificationAreas() {
      return [notificationAreas.GALLERY];
    },
    observerOptions() {
      return { rootMargin: '50%' };
    },
    isObservable() {
      return this.albums.length > 0;
    },
    isProfileSwitched() {
      return this.originalProfilePortalRole != this.profile.role;
    },
    getFilterInstProfileIds() {
      if (this.selectedInstitutionCodeForFilter) {
        if (this.profile.role === portalRoles.OTP || this.profile.role === portalRoles.GUARDIAN) {
          const childrenFromSelectedInstitution = this.children.filter(
            child => child.institutionCode === this.selectedInstitutionCodeForFilter
          );
          return childrenFromSelectedInstitution.map(child => child.id);
        }
        return this.institutions
          .filter(institution => institution.institutionCode === this.selectedInstitutionCodeForFilter)
          .map(institution => institution.institutionProfileId);
      } else {
        if (this.profile.role === portalRoles.OTP || this.profile.role === portalRoles.GUARDIAN) {
          return this.activeChildren;
        }
        return this.institutions
          .filter(institution => this.activeInstitutions.includes(institution.institutionCode))
          .map(institution => institution.institutionProfileId);
      }
    },
    sortConfigOptions() {
      const sortConfigOptions = [
        {
          value: albumSortType.CREATED_AT,
          name: 'label.sort_by_created_date',
          asc: 'label.sort_asc',
          dsc: 'label.sort_dsc',
        },
        {
          value: albumSortType.TITLE,
          name: 'label.sort_by_album_name',
          asc: 'label.sort_a_z',
          dsc: 'label.sort_z_a',
        },
      ];
      if (!Cookie.Read('fromAdminGroup')) {
        sortConfigOptions.unshift({
          value: albumSortType.MEDIA_CREATED_AT,
          name: 'label.sort_by_newest_media',
          asc: 'label.sort_last',
          dsc: 'label.sort_first',
        });
      }
      return sortConfigOptions;
    },
    selectedAlbums() {
      return this.albums.filter(album => album.selected);
    },
    isUploadParent() {
      return this.parent === parentTypes.UPLOAD;
    },
    isGalleryParent() {
      return this.parent === parentTypes.GALLERY;
    },
    isGroupParent() {
      return this.parent === parentTypes.GROUP;
    },
    isAnySelectedAlbums() {
      return this.selectedAlbums.length > 0;
    },
    isSingleSelectedAlbum() {
      return this.selectedAlbums.length === 1;
    },
    selectedAlbumsTitle() {
      const albumsTitle = [];
      this.selectedAlbums.forEach(selectedAlbum => {
        albumsTitle.push(selectedAlbum.title);
      });
      return albumsTitle;
    },
    canDeleteSelectedAlbum() {
      return this.selectedAlbums.every(album => album.currentUserCanDelete);
    },
    mobileTopbarRightText() {
      if (this.selectMode && this.isAnySelectedAlbums) {
        return 'NEXT_STEP';
      }
      if (this.isUploadParent) {
        return 'OK';
      }
      return '';
    },
    isCreator() {
      const selectedAlbumsCreatorIds = this.selectedAlbums.map(album => album.creator.id);
      return this.institutions.some(inst => selectedAlbumsCreatorIds.includes(inst.institutionProfileId));
    },
    isStepUpRequired() {
      if (this.isCreator) {
        return false;
      }
      return !this.isSteppedUp;
    },
  },
  watch: {
    activeInstitutions: function () {
      if (!this.isProfileSwitched) {
        this.refresh();
      }
    },
    activeChildren: function () {
      if (!this.isProfileSwitched) {
        this.refresh();
      }
    },
  },
  created() {
    this.originalProfilePortalRole = this.profile.role;
  },
  mounted() {
    this.initializeAlbums();
    this.scrollToSelectedAlbum();
  },
  methods: {
    ...mapActions({
      downloadZipFile: types.DOWNLOAD_ZIP_FILE,
      getAlbums: types.ACTION_GET_ALBUMS,
      getAlbumsAdmin: types.ACTION_GET_ALBUMS_ADMIN,
      loadAlbumMedia: types.ACTION_GET_ALBUM_DETAILS,
      loadAlbumMediaAdmin: types.ACTION_GET_ALBUM_DETAILS_ADMIN,
      deleteNotifications: types.DELETE_NOTIFICATIONS,
      deleteAlbum: types.DELETE_ALBUM,
    }),
    ...mapMutations({
      setErrorText: types.MUTATE_ERROR_TEXT,
      resetAlbums: types.MUTATE_RESET_ALBUMS,
      setHasMediaChanged: types.MUTATE_HAS_MEDIA_CHANGED,
      mutateFilterBy: types.MUTATE_GALLERY_FILTER_BY,
      mutateSortOn: types.MUTATE_GALLERY_SORT_ON,
      mutateOrderDirection: types.MUTATE_GALLERY_ORDER_DIRECTION,
    }),
    handleAlbumDetailHidden() {
      this.showAlbumDetailForUpload = false;
      this.scrollToSelectedAlbum();
    },
    mutateDefaultParams() {
      const sortOn = Cookie.Read('fromAdminGroup') ? albumSortType.CREATED_AT : albumSortType.MEDIA_CREATED_AT;
      const orderDirection = Cookie.Read('fromAdminGroup') ? orderDirections.ASCENDING : orderDirections.DESCENDING;
      this.mutateFilterBy(albumFilterType.ALL);
      this.mutateSortOn(sortOn);
      this.mutateOrderDirection(orderDirection);
    },
    initializeAlbums() {
      this.currentIndex = this.albums.length - 1;
      this.selectMode = this.isUploadParent;
      const isRefreshed = this.selectMode || this.albums.length === 0;
      if (isRefreshed) {
        this.mutateDefaultParams();
        this.refresh();
      }
    },
    scrollToSelectedAlbum() {
      if (!this.activeAlbum) {
        return;
      }
      this.$nextTick(() => {
        this.$el.querySelector(`#album-${this.activeAlbum.id}`)?.scrollIntoView({ block: 'center' });
      });
    },
    onBottomReached() {
      if (this.isLoading || !this.moreAlbumsExist) {
        return;
      }
      this.loadAlbums();
    },
    openAlbum(album) {
      if (this.isGalleryParent) {
        if (!album.id) {
          if (album.regardingInstitutionProfileId != null) {
            this.$router.push({
              name: 'institutionProfileMedias',
              params: {
                institutionProfileId: album.regardingInstitutionProfileId,
              },
            });
          } else {
            this.$router.push({ name: 'myMedias' });
          }
        } else {
          this.removeGalleryNotification([
            {
              type: notificationEventTypes.ALBUM_SHARED,
              albumId: album.id,
            },
            {
              type: notificationEventTypes.MEDIA_ADDED_TO_ALBUM,
              albumId: album.id,
            },
          ]);
          this.$router.push({
            name: 'albumDetails',
            params: { albumId: album.id },
          });
        }
      } else if (this.isGroupParent) {
        this.$router.push({
          name: 'group-album-details',
          params: { albumId: album.id },
        });
      } else {
        if (album.id == null) {
          this.showMyMediaAlbumForUpload = true;
          this.selectedAlbumId = null;
        } else {
          this.showMyMediaAlbumForUpload = false;
          this.selectedAlbumId = album.id;
        }
        this.showAlbumDetailForUpload = true;
      }
    },
    async downloadAlbums() {
      if (this.selectedAlbums.length === 0) {
        return;
      }

      this.isDownloading = true;
      const isSingleAlbum = this.isSingleSelectedAlbum;
      const selectedAlbumsToDownload = await Promise.all(
        this.selectedAlbums.map(async album => ({
          title: album.title,
          albumMedias: await this.getAlbumMedia(album.id),
        }))
      );

      await AlbumDownloader.downloadAlbums(
        'download',
        selectedAlbumsToDownload,
        true,
        () => {
          this.$showToast(
            isSingleAlbum ? this.$t('gallery.download_success_single') : this.$t('gallery.download_success_multiple')
          );
        },
        () => {
          this.$showToast(this.$t('gallery.download_error'), 'error');
        }
      );

      this.isDownloading = false;
      this.isVisibleGalleryActionMobile = false;
      this.$refs.downloadAlbumWarningModal.hide();
      this.deselectAllAlbum();
    },
    async deleteAlbums() {
      if (this.selectedAlbums.length === 0) {
        return;
      }

      this.isDeleting = true;
      const isSingleAlbum = this.isSingleSelectedAlbum;
      const deleteAlbumIds = this.selectedAlbums.map(album => album.id);
      let result = false;

      try {
        result = await this.deleteAlbum({ albumIds: deleteAlbumIds });
      } catch {
        this.$showToast(this.$t('gallery.delete_error_multiple'), 'error');
      }

      if (result) {
        this.$showToast(
          isSingleAlbum ? this.$t('gallery.delete_success_single') : this.$t('gallery.delete_success_multiple')
        );
      } else {
        this.$showToast(this.$t('gallery.delete_error_multiple'), 'error');
        this.refresh();
      }

      if (this.isGroupParent) {
        this.$router.push({
          name: 'group-gallery',
          params: { groupId: this.activeGroup.id },
        });
      }

      this.isDeleting = false;
      this.isVisibleGalleryActionMobile = false;
      this.$refs.deleteAlbumWarningModal.hide();
      this.deselectAllAlbum();
    },
    async getAlbumMedia(albumId) {
      const albumMedia = [];
      let mediaCount = 0;
      const params = {
        albumId: albumId,
        index: 0,
        limit: 10,
      };
      let loadAlbumMediaAction = this.loadAlbumMedia;
      if (Cookie.Read('fromAdminGroup')) {
        loadAlbumMediaAction = this.loadAlbumMediaAdmin;
      }
      do {
        const albumMediaResponse = await loadAlbumMediaAction(params);
        const mediaResults = albumMediaResponse.results;
        albumMedia.push(...mediaResults);
        params.index = albumMedia.length;
        mediaCount = albumMediaResponse.mediaCount;
      } while (params.index < mediaCount);
      return albumMedia;
    },
    // These 3 methods to solve an issue with pagination in AULA-34760,
    getRequestLimit() {
      // AULA-34760: Get one extra album to check if more albums exist,
      // the extra album has already been added by the Back-end in the 1st page (album for tagged media)
      let pageSize = this.pageSize;
      if (this.currentIndex > -1 || this.isGroupParent) {
        pageSize++;
      }
      return pageSize;
    },
    getRequestOffset() {
      if (!this.isGalleryParent) {
        return this.currentIndex + 1;
      }
      return this.currentIndex === -1 ? 0 : this.currentIndex;
    },
    increaseRequestOffsetValue() {
      this.currentIndex = this.currentIndex + this.pageSize;
    },
    loadAlbums: function () {
      if (this.loadGalleryCancelTokenSource != null) {
        this.loadGalleryCancelTokenSource.cancel();
        this.resetLoadGalleryCancelTokenSource();
      }
      this.isLoading = true;

      let getAlbumFunction = this.getAlbums;
      if (Cookie.Read('fromAdminGroup')) {
        getAlbumFunction = this.getAlbumsAdmin;
      }
      getAlbumFunction(this.getAlbumHttpConfigurations()).then(
        () => {
          this.isLoading = false;
          this.resetLoadGalleryCancelTokenSource();
          this.increaseRequestOffsetValue();
        },
        () => null
      );
    },
    refresh() {
      this.currentIndex = -1;
      this.resetAlbums();
      this.setHasMediaChanged(false);
      this.loadAlbums();
    },
    doSort(sort, sortOn) {
      this.sortName = sortOn;
      if (this.sortOn === sort) {
        const orderDirection =
          this.orderDirection === orderDirections.ASCENDING ? orderDirections.DESCENDING : orderDirections.ASCENDING;
        this.mutateOrderDirection(orderDirection);
      } else if (sort === albumSortType.MEDIA_CREATED_AT) {
        this.mutateSortOn(sort);
        this.mutateOrderDirection(orderDirections.DESCENDING);
      } else {
        this.mutateSortOn(sort);
        this.mutateOrderDirection(orderDirections.ASCENDING);
      }
      this.refresh();
    },
    doFilter(filterBy) {
      this.mutateFilterBy(filterBy);
      this.isFiltered = true;
      this.refresh();
    },
    getAlbumHttpConfigurations() {
      this.createSourceForLoadGalleryCancelTokenSource();
      const config = {
        params: {
          index: this.getRequestOffset(),
          limit: this.getRequestLimit(),
          sortOn: this.sortOn,
          orderDirection: this.orderDirection,
          filterBy: this.filterBy,
        },
        cancelToken: this.loadGalleryCancelTokenSource.token,
      };
      if (this.isGroupParent) {
        config.params.groupId = this.$route.params.groupId;
      } else {
        config.params.filterInstProfileIds = this.getFilterInstProfileIds;
      }
      return config;
    },
    emitSelectedMediaForEditor() {
      const selected = [];
      for (const album of this.albums) {
        if (album.selected) {
          selected.push(album);
        }
      }
      this.$emit('selectedAlbumsUpdated', selected);
    },
    removeGalleryNotification(removeNotifications) {
      let notificationsToRemove = [];
      for (const removeNotification of removeNotifications) {
        const notificationsForEachTpye = this.notifications.filter(
          notification =>
            notification.notificationEventType == removeNotification.type &&
            notification.albumId == removeNotification.albumId
        );
        if (notificationsForEachTpye.length > 0) {
          notificationsToRemove = [...notificationsToRemove, ...notificationsForEachTpye];
        }
      }
      if (notificationsToRemove.length > 0) {
        const notifications = notificationsToRemove.map(function (notification) {
          return {
            notificationId: notification.notificationId,
            institutionProfileId: notification.institutionProfileId,
          };
        });
        if (notifications.length > 0) {
          this.deleteNotifications({ notifications: notifications });
        }
      }
    },
    resetLoadGalleryCancelTokenSource() {
      this.loadGalleryCancelTokenSource = null;
    },
    createSourceForLoadGalleryCancelTokenSource() {
      this.loadGalleryCancelTokenSource = axios.CancelToken.source();
    },
    isFilterSelected(filter) {
      return filter.value === this.filterBy;
    },
    selectAlbum(album) {
      if (album.id === null) {
        return;
      }
      album.selected = !album.selected;
      if (this.isUploadParent) {
        this.emitSelectedMediaForEditor();
      }
    },
    toggleTagMode() {
      if (!this.selectMode) {
        this.deselectAllAlbum();
      }
    },
    deselectAllAlbum() {
      for (const album of this.albums) {
        album.selected = false;
      }
    },
    onDownloadAlbumClicked() {
      this.$refs.downloadAlbumWarningModal.show();
    },
    onDeleteAlbumClicked() {
      if (this.isStepUpRequired) {
        this.$refs.stepUpModal.show();
        return;
      }
      this.$refs.deleteAlbumWarningModal.show();
    },
    onReturnClicked() {
      this.isVisibleGalleryActionMobile = false;
    },
    onMobileTopbarRightTextClicked() {
      this.isVisibleGalleryActionMobile = true;
    },
    onMobileTopbarLeftIconClicked() {
      this.selectMode = false;
    },
    redirectToStepUp() {
      localStorage.setItem('redirectedurlafterstepup', window.location.href);
      localStorage.setItem('redirectedurlbeforestepup', window.location.href);
      window.location.href = '/auth/stepup.php';
    },
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
@use '../../../shared/assets/scss/core/variables' as *;
@use '../../../shared/assets/scss/core/breakpoints' as *;
@use '../../../shared/assets/scss/components/gallery/gallery' as *;

.gallery-container {
  .mobile-topbar-container {
    height: 60px;
    position: fixed;
    top: 0;
    z-index: $mobile-gallery-toolbar-z-index;

    ::v-deep .mid-column {
      font-size: 18px;
      font-weight: bold;
    }
  }

  .gallery-multiselect {
    @include breakpoint-lg-down {
      padding-left: 35px;
    }

    ::v-deep .switch-label {
      margin: 0 10px !important;
    }

    .action-button {
      position: relative;
      display: flex;
      align-items: center;
      gap: 5px;
      font-size: inherit;
      padding: 0 5px;
      margin: 0 5px;

      .download-spinner {
        position: absolute;
        right: -40px;
        width: 50px;
        height: 50px;
      }
    }
  }
}
</style>
